import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Splitter } from "@progress/kendo-react-layout";
import {
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
  ToolbarSpacer,
  Button,
  DropDownButton,
} from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { Input, Rating } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { DropDownListById } from "../../forms/form-components";
import { AlertNotification } from "./alertnotification";
import { List } from "../../components/list";
import { formatDateTime } from "../../lib/utils";
import parse from "html-react-parser";
import { AlertNotificationDashboard } from "./alertnotificationdashboard";
import { bellIcon } from "@progress/kendo-svg-icons";

import * as htmlToImage from "html-to-image";
import download from "downloadjs";

import i18next from "i18next";
import { useTranslation } from "react-i18next";

const notificationsItemRenderer = (currentTheme, t) => (props, item, index) => {
  const icon =
    item.Type == 1
      ? "k-i-warning"
      : item.Type == 3
        ? "k-i-data"
        : item.Type == 4
          ? "k-i-edit"
          : "k-i-email";
  const markIcon = item.Status == -1 ? "k-i-check-outline" : "k-i-check-circle";
  const bgColor = item.Status == -1 ? "transparent" : currentTheme.componentBg;
  const markText =
    item.Status == -1 ? t("notifications.read") : t("notifications.unread");

  const onMarkAsRead = props.onMarkAsRead;
  const onMarkAsUnread = props.onMarkAsUnread;
  const onRemove = props.onRemove;

  const iconStatus =
    item.DatasetRelatedStatus == "Started"
      ? "k-i-play"
      : item.DatasetRelatedStatus == "Succeeded"
        ? "k-i-validation-data"
        : item.DatasetRelatedStatus == "Failed"
          ? "k-i-warning-circle"
          : item.DatasetRelatedStatus == "InProgress"
            ? "k-i-pause"
            : "k-i-info-circle";

  const colorStatus =
    item.DatasetRelatedStatus == "Started"
      ? currentTheme.touchColor
      : item.DatasetRelatedStatus == "Succeeded"
        ? "green"
        : item.DatasetRelatedStatus == "Failed"
          ? "red"
          : item.DatasetRelatedStatus == "InProgress"
            ? currentTheme.touchColor
            : currentTheme.touchColor;
  const datasetRelatedStatus =
    {
      Started: t("notifications.started"),
      Succeeded: t("notifications.succeeded"),
      Failed: t("notifications.failed"),
      InProgress: t("notifications.in_progress"),
    }[item.DatasetRelatedStatus] || item.DatasetRelatedStatus;
  return (
    <div
      key={item.Id}
      className={`list-item`}
      style={{}}
      onDoubleClick={(e) => props.onEntityDoubleClick(item.Id)}
    >
      <div
        style={{
          display: "flex",
          alignItems: "Center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "Center",
            justifyContent: "flex-start",
          }}
        >
          <div
            className={`list-item-icon k-icon ${icon}`}
            style={{ color: currentTheme.componentText }}
          ></div>
          <div
            className="list-item-title"
            style={{ marginLeft: 6, color: currentTheme.componentText }}
          >
            {item.Title}
          </div>
        </div>
        <div className="list-item-buttons">
          <Tooltip anchorElement="target" position="left">
            <div
              title={markText}
              className={`list-item-mark k-icon ${markIcon}`}
              onClick={(e) => {
                if (item.Status == -1) onMarkAsRead(item.Id);
                else onMarkAsUnread(item.Id);
              }}
            ></div>
          </Tooltip>
          <Tooltip anchorElement="target" position="left">
            <div
              title={t("notifications.delete")}
              style={{ color: currentTheme.touchColor }}
              className="list-item-remove k-icon k-i-delete"
              onClick={(e) => onRemove(item.Id)}
            ></div>
          </Tooltip>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 0,
          marginTop: 10,
        }}
      >
        <div className="list-item-initiated-by">
          {t("notifications.initiated_by", { user: item.InitiatedBy })}
        </div>
        <div className="list-item-initiated-on">
          {formatDateTime(item.InitiatedOn, false)}
        </div>
      </div>
      <div className="list-item-toolbar">
        <div className="list-item-importance">
          <Rating
            max={3}
            value={item.Importance == 1 ? 1 : item.Importance == 3 ? 2 : 3}
            themeColor={"primary"}
            readonly={true}
            size={"small"}
          />
        </div>
        <div
          style={{
            display: "flex",
            color: colorStatus,
          }}
        >
          <div
            className={`list-item-icon k-icon ${iconStatus}`}
            title={datasetRelatedStatus}
            style={{ margin: "2px 3px 0 0", color: colorStatus }}
          ></div>
          {datasetRelatedStatus}
        </div>
      </div>
    </div>
  );
};

const mobileNotificationsItemRenderer = (currentTheme, t) => (props, item) => {
  const icon =
    item.Type == 1
      ? "k-i-warning"
      : item.Type == 3
        ? "k-i-data"
        : item.Type == 4
          ? "k-i-edit"
          : "k-i-email";
  const type =
    item.Type == 1
      ? t("header.alerts")
      : item.Type == 2
        ? t("notifications.messages")
        : item.Type == 3
          ? t("header.data")
          : item.Type == 4
            ? t("header.datacorrection")
            : t("notifications.all");

  return (
    <button
      type="button"
      key={item.Id}
      className="mobile-notification-item"
      onClick={() => props.onEntitySelected(item.Id)}
    >
      <span className={`mobile-notification-icon k-icon ${icon}`} />
      <span className="mobile-notification-content">
        <span className="mobile-notification-title">{item.Title}</span>
        <span className="mobile-notification-meta">
          {type} · {formatDateTime(item.InitiatedOn, false)}
        </span>
      </span>
      <span className="mobile-notification-next k-icon k-i-arrow-chevron-right" />
    </button>
  );
};

const MobileNotifications = ({
  currentTheme,
  pageState,
  t,
  types,
  typeId,
  ...props
}) => {
  if (pageState.formValues.Id) {
    return (
      <div className="notifications-mobile-view notifications-mobile-details">
        <AlertNotification
          currentTheme={currentTheme}
          pageState={pageState}
          onMarkAsRead={props.onMarkAsRead}
          onMarkAsUnread={props.onMarkAsUnread}
          onRemove={props.onRemove}
          onClose={props.onClose}
          onToolsToggle={props.onToolsToggle}
          isMobile={true}
        />
      </div>
    );
  }

  return (
    <div className="notifications-mobile-view">
      <div className="notifications-mobile-heading">
        <h1>{t("notifications.notifications")}</h1>
        <p>
          {t(
            "notifications.mobileSubtitle",
            "Alerts, messages and data activity",
          )}
        </p>
      </div>
      <div className="notifications-mobile-tabs" role="tablist">
        {types.map((type) => (
          <Button
            key={type.Id}
            type="button"
            fillMode="flat"
            className={`notifications-mobile-tab ${
              typeId == type.Id ? "active" : ""
            }`}
            aria-pressed={typeId == type.Id}
            onClick={() => props.onTypeChange(type.Id)}
          >
            {type.Title}
          </Button>
        ))}
      </div>
      <div>
        <List
          className="notifications-mobile-list"
          items={pageState.items}
          itemRenderer={mobileNotificationsItemRenderer(currentTheme, t)}
          onEntitySelected={props.onEntitySelected}
        />
      </div>
    </div>
  );
};

export const NotificationsPageBody = (props) => {
  const { onSplitterChange, ...other } = props;
  const { t } = useTranslation();
  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.notificationsPage);
  const appearance = useSelector((state) => state.appearance);
  const typeId = props.typeId;
  const title = props.title;
  const importance = props.importance;
  const toolsVisibleIcon = pageState.toolsVisible ? "unpin" : "pin";
  const toolsVisibleTitle = pageState.toolsVisible
    ? t("notifications.hide")
    : t("notifications.show");

  const defaultType = { Id: 0, Title: t("notifications.all") };
  const types = [
    { ...defaultType },
    { Id: 1, Title: t("header.alerts") },
    { Id: 2, Title: t("notifications.messages") },
    { Id: 3, Title: t("header.data") },
    { Id: 4, Title: t("header.datacorrection") },
  ];

  const alertImportancesFilter = [
    { Id: 0, Title: t("extras.all") },
    { Id: 1, Title: t("notifications.unimportant") },
    { Id: 3, Title: t("notifications.important") },
    { Id: 5, Title: t("notifications.critical") },
    { Id: 35, Title: t("extras.impandcrit") },
  ];

  //snapshot
  const contentZoneRef = useRef(null);

  const getSnapshot = async (e) => {
    let w = parseInt(contentZoneRef.current.innerWidth);
    let h = parseInt(contentZoneRef.current.innerHeight); //parseInt(w * 0.75);

    let dataUrl = null;

    try {
      dataUrl = await htmlToImage.toJpeg(contentZoneRef.current, {
        width: w,
        height: h,
        quality: 0.9, //skipAutoScale: true,
        //backgroundColor: "#002240"
      });

      return dataUrl;
    } catch (ex) {
      return null;
    }
  };

  const handleOnSnapshot = async (e) => {
    let dataUrl = await getSnapshot();
    if (dataUrl) download(dataUrl, "vas-alerts.jpg");
  };

  return (
    <Fragment>
      {pageState.processing && (
        <div className="page-loader">
          <Loader
            size={"large"}
            type="infinite-spinner"
            themeColor={"primary"}
          />
          {pageState.processingText && (
            <div style={{ fontSize: 13, color: currentTheme.primary }}>
              {pageState.processingText}
            </div>
          )}
        </div>
      )}
      {pageState.pageLoaded && (
        <Fragment>
          <div className="notifications-desktop-view">
            <Splitter
              style={{ border: "none" }}
              panes={pageState.panes}
              onChange={onSplitterChange}
            >
              <div className="notifications-page-manager">
                <div className="notifications-page-left-top">
                  <div
                    style={{
                      fontWeight: 500,
                      color: `${currentTheme.touchColor}`,
                    }}
                  >
                    {t("notifications.filter")}
                  </div>
                  <div style={{ display: "flex", marginTop: 4 }}>
                    <DropDownListById
                      style={{ flex: 1 }}
                      data={types}
                      dataItemKey="Id"
                      textField="Title"
                      value={typeId}
                      onChange={(e) => props.onTypeIdChange(e.value)}
                    />
                  </div>
                  <div style={{ display: "flex", marginTop: 10 }}>
                    <Input
                      style={{ marginRight: 8, flex: 1 }}
                      value={title}
                      onChange={(e) => props.onTitleChange(e.value)}
                      placeholder={t("notifications.filter1")}
                    />
                    <DropDownListById
                      style={{ flex: 1, marginLeft: 2, marginRight: 6 }}
                      data={alertImportancesFilter}
                      dataItemKey="Id"
                      textField="Title"
                      value={importance}
                      onChange={(e) => props.onImportanceChange(e.value)}
                    />
                    <Button
                      icon="zoom"
                      style={{ color: currentTheme.touchColor }}
                      size="small"
                      onClick={(e) => props.onFilter()}
                    ></Button>
                  </div>
                  <div style={{ display: "flex", marginTop: 10 }}>
                    <Button
                      icon="trash"
                      style={{ color: currentTheme.touchColor }}
                      size="small"
                      onClick={(e) =>
                        props.onRemoveNotifications(typeId, title, importance)
                      }
                    >
                      {t("notifications.deletenot1")}
                    </Button>
                  </div>
                </div>
                <div className="notifications-page-left-middle">
                  <List
                    className="notifications"
                    items={pageState.items}
                    itemRenderer={notificationsItemRenderer(currentTheme, t)}
                    onMarkAsRead={props.onMarkAsRead}
                    onMarkAsUnread={props.onMarkAsUnread}
                    onRemove={props.onRemove}
                    onEntityDoubleClick={props.onEntitySelected}
                  />
                </div>
                <div className="notifications-page-left-bottom">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderTop: `1px solid ${currentTheme.separatorColor}`,
                      paddingTop: 6,
                      paddingBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        icon="arrow-end-left"
                        size="small"
                        style={{ color: currentTheme.touchColor, fontSize: 24 }}
                        disabled={pageState.currentPageIndex <= 0}
                        onClick={props.onMoveFirst}
                      ></Button>
                      <Button
                        icon="arrow-60-left"
                        size="small"
                        style={{
                          marginLeft: 4,
                          color: currentTheme.touchColor,
                          fontSize: 24,
                        }}
                        disabled={pageState.currentPageIndex <= 0}
                        onClick={props.onMovePrevious}
                      ></Button>
                      <div
                        style={{
                          width: 100,
                          textAlign: "center",
                          fontWeight: 500,
                          fontSize: 13,
                        }}
                      >
                        {pageState.currentPageIndex + 1} /{" "}
                        {pageState.pagesCount}
                      </div>
                      <Button
                        icon="arrow-60-right"
                        size="small"
                        style={{ color: currentTheme.touchColor, fontSize: 24 }}
                        disabled={
                          pageState.currentPageIndex >= pageState.pagesCount - 1
                        }
                        onClick={props.onMoveNext}
                      ></Button>
                      <Button
                        icon="arrow-end-right"
                        size="small"
                        style={{
                          marginLeft: 4,
                          color: currentTheme.touchColor,
                          fontSize: 24,
                        }}
                        disabled={
                          pageState.currentPageIndex >= pageState.pagesCount - 1
                        }
                        onClick={props.onMoveLast}
                      ></Button>
                    </div>
                  </div>
                </div>
              </div>

              {pageState.formValues.Id && (
                <div className="notification-wrapper">
                  <AlertNotification
                    currentTheme={currentTheme}
                    pageState={pageState}
                    onMarkAsRead={props.onMarkAsRead}
                    onMarkAsUnread={props.onMarkAsUnread}
                    onRemove={props.onRemove}
                    onClose={props.onClose}
                    onToolsToggle={props.onToolsToggle}
                  />
                </div>
              )}
              {!pageState.formValues.Id && (
                <div className="notification-wrapper">
                  <div
                    className={`toolbar ${
                      appearance.isRightToLeft ? "rtl" : "ltr"
                    }`}
                  >
                    <Toolbar>
                      <Fragment>
                        <ToolbarItem>
                          <Button
                            disabled={pageState.processing}
                            style={{ color: currentTheme.primary }}
                            icon={toolsVisibleIcon}
                            onClick={props.onToolsToggle}
                          >
                            {toolsVisibleTitle}
                          </Button>
                        </ToolbarItem>
                        <ToolbarSeparator />
                        <ToolbarItem>
                          <Button
                            disabled={pageState.processing}
                            style={{ color: currentTheme.primary }}
                            icon={"bell"}
                            onClick={props.onManageAlert}
                          >
                            {t("notifications.manage")}
                          </Button>
                        </ToolbarItem>
                        <ToolbarSeparator />
                        <ToolbarItem>
                          <Button
                            disabled={pageState.processing}
                            className="snapshot"
                            style={{}}
                            title={t("notifications.snapshot")}
                            icon={"photo-camera"}
                            onClick={handleOnSnapshot}
                          ></Button>
                        </ToolbarItem>
                        <ToolbarSeparator />
                        <ToolbarItem>
                          <Button
                            disabled={pageState.processing}
                            style={{ color: currentTheme.primary }}
                            icon={"close"}
                            onClick={props.onCloseNotfication}
                          >
                            {t("notifications.close")}
                          </Button>
                        </ToolbarItem>
                      </Fragment>
                    </Toolbar>
                  </div>
                  <div ref={contentZoneRef}>
                    <AlertNotificationDashboard
                      onEntitySelected={props.onEntitySelected}
                    />
                  </div>
                </div>
              )}
            </Splitter>
          </div>
          <MobileNotifications
            currentTheme={currentTheme}
            pageState={pageState}
            t={t}
            types={types}
            typeId={typeId}
            onTypeChange={props.onMobileTypeChange}
            onEntitySelected={props.onEntitySelected}
            onMarkAsRead={props.onMarkAsRead}
            onMarkAsUnread={props.onMarkAsUnread}
            onRemove={props.onRemove}
            onClose={props.onClose}
            onToolsToggle={props.onToolsToggle}
          />
        </Fragment>
      )}
      <style>
        {`  
            .notification-wrapper{
                      border: ${currentTheme.pageFormBorder};   
                      height:100%;
                        direction:${appearance.isRightToLeft ? "rtl" : "ltr"}
                      
            }              
                      
            .notifications-page .k-splitter{
                        flex-direction:${
                          appearance.isRightToLeft ? "row-reverse" : "row"
                        } 
                        }

                           .notifications-page .k-splitter .k-pane{
                        direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                        }
                    /*the manager*/
                    .notifications-page > .content .notifications-page-manager
                    {
                        display: flex;
                        flex-direction: column;
                        flex-wrap: nowrap;
                        height: 100%;
                        overflow: hidden;/* auto;*/

                        border: ${currentTheme.managerBorder};
                        border-radius: ${currentTheme.pageBorderRadius};
                        background-color: ${currentTheme.componentBgFaded};
                        direction:${appearance.isRightToLeft ? "rtl" : "ltr"}
                    }

                    .notifications-page > .content .notifications-page-manager .k-dropdownlist:focus,
                    .notifications-page > .content .notifications-page-manager .k-input:focus
                    {
                        box-shadow: none;
                    }

                    .notifications-page > .content .notifications-page-manager .k-dropdownlist,
                    .notifications-page > .content .notifications-page-manager .k-input,
                    .notifications-page > .content .notifications-page-manager .k-input::placeholder
                    {
                        background-color: transparent;
                        color: ${currentTheme.componentText};
                        direction: ${
                          appearance.isRightToLeft
                            ? "rtl !important"
                            : "ltr !important"
                        }
                    }

                    .k-animation-container{
                      direction: ${
                        appearance.isRightToLeft
                          ? "rtl !important"
                          : "ltr !important"
                      }
                    }
                    .notifications-page > .content .notifications-page-left-top
                    {
                        display: flex;
                        flex-direction: column;
                        align-items: stretch;
                        padding: 16px 12px;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                    }

                    .notifications-page > .content .notifications-page-left-middle
                    {
                        flex: 1;
                        overflow-y: auto;
                        overflow-x: hidden;
                    }

                    .notifications-page > .content .notifications-page-left-bottom
                    {

                    }

                    .notifications-page > .content .notifications-page-left-middle .list-container
                    {
                        height: 100%;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-root
                    {
                        padding: 0 0px;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-root > div > div
                    {
                        padding:0;/*cancel table effect*/
                    }
                    
                    .notifications-page > .content .notifications-page-left-middle .list-item
                    {
                        display: flex;
                        flex-direction: column;
                        padding: 12px 8px 8px 8px;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        background-color: transparent;
                    }                    
                    
                    .notifications-page > .content .notifications-page-left-middle .list-item:hover
                    {
                        cursor: pointer;
                        background-color: ${currentTheme.componentBgFaded};
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-icon
                    {
                        color: ${currentTheme.primary};
                        font-weight: 500;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-title
                    {
                        color: ${currentTheme.primary};
                        font-weight: 500;
                        font-size: 13px;
                        width: 314px;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        margin-top:-3px;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-initiated-by
                    {
                        margin-left: 0px;
                        width: 175px;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        font-size: 12px;
                        font-weight: 500;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-initiated-on
                    {
                        font-size: 12px;
                        font-weight: 500;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-toolbar
                    {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 8px;
                        padding:0;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-importance
                    {
                        flex: 1;
                        margin-left: 0px;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-importance .k-rating-container
                    {
                        margin-left: 0px;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-importance .k-rating-container > span:first-child
                    {
                        padding-left: 0;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-importance .k-icon
                    {
                        font-size: 12px;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-buttons
                    {
                        display: flex;
                        align-items: center;
                        margin-top: -4px;
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-mark
                    {
                        color: ${currentTheme.primary};
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-remove
                    {
                        margin-left:16px;
                        color: ${currentTheme.primary};
                    }

                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-mark:hover,
                    .notifications-page > .content .notifications-page-left-middle .list-item .list-item-remove:hover
                    {
                        cursor: pointer;
                    }

                     /*toolbar*/
                    .notifications-page .toolbar .k-toolbar
                    {
                      width: 100%;
                      border-left: none !important;
                      border-bottom: 1px solid ${currentTheme.separatorColor};
                      border-top: none !important;
                      border-right: none !important;
                      padding: 3px 4px 3px 0px;
                      background-color: ${
                        currentTheme.toolbarBg
                      };/*transparent;*/
                    }

                    .notifications-page .toolbar.rtl .k-toolbar[dir="ltr"]
                    {
                      direction: rtl !important; 
                    }
                    .notifications-page .toolbar.ltr .k-toolbar[dir="rtl"]
                    {
                      direction: ltr !important;
                    }

                    .notifications-page .toolbar .k-button
                    {
                      /*the toolbar buttons*/
                      border: none;
                      padding: 6px 8px;
                      background-color:transparent;
                    }

                    .notifications-page .toolbar .k-dropdown-button > button
                    {
                      /*the toolbar buttons*/
                      border: none;
                      padding: 6px 8px;
                      background-color: transparent;
                    }

                    .toolbar-item-schema li
                    {
                        padding: 6px !important;
                    }

                    .notifications-page .toolbar .k-separator 
                    {
                        border-color: ${currentTheme.primary}
                    }

                    .notifications-page .toolbar .k-toolbar .k-split-button:focus, .form-root .toolbar .k-toolbar .k-split-button.k-focus,
                    .notifications-page .toolbar .k-toolbar .k-button-solid-base:focus, .form-root .toolbar .k-toolbar .k-button-solid-base.k-focus
                    {
                        box-shadow: none !important;
                        border: none !important;
                    }

                    .notifications-page .toolbar .k-toolbar .k-button:active::before, .notifications-page .toolbar .k-toolbar .k-button:hover::before,
                    .notifications-page .toolbar .k-toolbar .k-button:focus::before, .notifications-page .toolbar .k-toolbar .k-button:focus-within::before                        
                    {
                        opacity: 0 !important;
                        border: none !important;
                        box-shadow: none !important;
                    }

                    .notifications-page .toolbar .k-toolbar .k-dropdown-button.k-focus .k-button::after
                    {
                        /*border: 1px solid ${
                          currentTheme.componentBg
                        } !important;*/
                        box-shadow: none !important;
                    }                       

                    .notifications-page .toolbar .k-toolbar .k-button .k-button-text
                    {
                        font-size: 13px !important;
                        font-weight: 400;
                        color: ${currentTheme.toolbarText};
                    }

                   .notifications-page .toolbar .k-button-icon
                    {
                      font-weight: 500;
                      color: ${currentTheme.toolbarText};
                    }

                    .notifications-page .toolbar .is-live-report .k-button-icon
                    {
                        padding: 2px 4px;
                        font-size: 20px;
                        /*color: ${
                          pageState.isLiveReport
                            ? currentTheme.success
                            : currentTheme.disabledText
                        }!important;*/
                    }

                    .notifications-page .toolbar .k-toolbar .k-button:hover .k-button-text
                    {
                        font-weight: 700;
                        font-size: 12.2px;
                    }

                    .notifications-page .toolbar .k-toolbar .is-favorite span
                    {
                        font-size: 18px;
                        font-weight: 700;
                    }
                    
                    .notifications-desktop-view {
                      height: 100%;
                    }

                    .notifications-mobile-view {
                      display: none;
                    }

                    @media (max-width: 1024px) {
                      .notifications-page > .content {
                        overflow-y: auto;
                      }

                      .notifications-desktop-view {
                        display: none;
                      }

                      .notifications-mobile-view {
                        width:100%;
                        display: flex;
                        flex-direction: column;
                        min-height: 100%;
                        padding: 18px 16px;
                        background: ${currentTheme.componentBg};
                        color: ${currentTheme.componentText};
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"};
                      }

                      .notifications-mobile-heading h1 {
                        margin: 0;
                        color: ${currentTheme.componentText};
                        font-size: 21px;
                        font-weight: 600;
                      }

                      .notifications-mobile-heading p {
                        margin: 5px 0 18px;
                        color: ${currentTheme.componentText};
                        font-size: 13px;
                        opacity: 0.72;
                      }

                      .notifications-mobile-tabs {
                        display: flex;
                        flex: 0 0 auto;
                        gap: 8px;
                        overflow-x: auto;
                        padding-bottom: 25px;
                        scrollbar-width: none;
                      }

                      .notifications-mobile-tabs::-webkit-scrollbar {
                        display: none;
                      }

                      .notifications-page .notifications-mobile-tab {
                        flex: 0 0 auto;
                        min-width: auto;
                        border: 1px solid ${currentTheme.separatorColor} !important;
                        border-radius: 999px;
                        padding: 6px 12px !important;
                        background: transparent;
                        color: ${currentTheme.componentText};
                        font-size: 12px;
                        line-height: 1.2;
                      }

                      .notifications-page .notifications-mobile-tab.active {
                        border-color: ${currentTheme.touchColor} !important ;
                        background: ${currentTheme.primary};
                        color: ${currentTheme.touchColor || currentTheme.primary};
                      }

                      .notifications-mobile-list,
                      .notifications-mobile-list .list-root {
                        width: 100%;
                      }

                      .notifications-page .notifications-mobile-details {
                        padding: 0;
                      }

                      .notifications-page .notifications-mobile-details .alert-notification {
                        min-height: 100%;
                      }

                      .notifications-page .notifications-mobile-details .alert-notification > .toolbar {
                        overflow-x: auto;
                      }

                      .notifications-page .notifications-mobile-details .alert-notification .content {
                        height: auto !important;
                        padding: 0 16px 16px;
                      }

                      .notifications-page .notifications-mobile-details .alert-notification .row {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 8px;
                      }

                      .notifications-page .notifications-mobile-details .alert-notification .row .column-left,
                      .notifications-page .notifications-mobile-details .alert-notification .row .column-right {
                        width: 100%;
                      }

                      .notifications-page .mobile-notification-item {
                        display: grid;
                        grid-template-columns: 24px minmax(0, 1fr) 24px;
                        gap: 9px;
                        align-items: center;
                        width: 100%;
                        padding: 13px 0;
                        border: 0;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        background: transparent;
                        color: ${currentTheme.componentText};
                        font: inherit;
                        text-align: start;
                        cursor: pointer;
                      }

                      .notifications-page .mobile-notification-icon {
                        color: ${currentTheme.primary};
                        font-size: 17px;
                      }

                      .mobile-notification-content {
                        display: flex;
                        min-width: 0;
                        flex-direction: column;
                        gap: 3px;
                      }

                      .mobile-notification-title,
                      .mobile-notification-meta {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                      }

                      .mobile-notification-title {
                        font-size: 14px;
                        font-weight: 600;
                      }

                      .mobile-notification-meta {
                        font-size: 12px;
                        opacity: 0.7;
                      }

                      .mobile-notification-next {
                        justify-self: end;
                        color: ${currentTheme.primary};
                        font-size: 15px;
                      }
                    }

                    /*form toolbar*/
                `}
      </style>
    </Fragment>
  );
};
